import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { FileSignature, Calendar, DollarSign, ExternalLink, Check, Clock, AlertTriangle, FileText, Download, ChevronDown, ChevronUp, Receipt } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const Contracts = () => {
  const { token } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedContract, setExpandedContract] = useState(null);
  const [contractDocuments, setContractDocuments] = useState({});
  const [downloadingBoleto, setDownloadingBoleto] = useState(null);
  const [downloadingContract, setDownloadingContract] = useState(null);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContractDocuments = async (contractId) => {
    if (contractDocuments[contractId]) return; // Already fetched
    
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractDocuments(prev => ({ ...prev, [contractId]: response.data }));
    } catch (error) {
      console.error('Error fetching contract documents:', error);
      setContractDocuments(prev => ({ ...prev, [contractId]: [] }));
    }
  };

  const toggleContractDocuments = async (contractId) => {
    if (expandedContract === contractId) {
      setExpandedContract(null);
    } else {
      setExpandedContract(contractId);
      await fetchContractDocuments(contractId);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
      active: { label: 'Ativo', color: 'bg-green-100 text-green-700' },
      inactive: { label: 'Inativo', color: 'bg-gray-100 text-gray-700' },
      judicialized: { label: 'Judicializado', color: 'bg-red-100 text-red-700' },
      completed: { label: 'Concluído', color: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-600' }
    };
    return configs[status] || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  const getPaymentStatus = (contract, index) => {
    const payments = contract.installment_payments || [];
    const storedStatus = payments[index] || 'pending';
    
    if (storedStatus === 'paid') return 'paid';
    if (storedStatus === 'cancelled') return 'cancelled';
    
    // If contract is cancelled, unpaid installments are cancelled
    if (contract.status === 'cancelled') return 'cancelled';
    
    const dueDate = contract.due_dates?.[index];
    if (dueDate && storedStatus !== 'paid') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Parse date string directly to avoid timezone issues
      const parts = dueDate.split('-');
      const dueDateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      dueDateObj.setHours(0, 0, 0, 0);
      
      if (today > dueDateObj) return 'overdue';
    }
    
    return storedStatus;
  };

  const getPaymentStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pendente', color: 'text-yellow-600', icon: Clock, bg: 'bg-yellow-100' },
      paid: { label: 'Pago', color: 'text-green-600', icon: Check, bg: 'bg-green-100' },
      overdue: { label: 'Em Atraso', color: 'text-red-600', icon: AlertTriangle, bg: 'bg-red-100' },
      cancelled: { label: 'Cancelada', color: 'text-gray-400', icon: AlertTriangle, bg: 'bg-gray-100' }
    };
    return configs[status] || configs.pending;
  };

  const calculateContractStats = (contract) => {
    let paidCount = 0;
    let overdueCount = 0;
    let paidAmount = 0;
    
    for (let i = 0; i < (contract.installments || 0); i++) {
      const status = getPaymentStatus(contract, i);
      if (status === 'paid') {
        paidCount++;
        paidAmount += contract.installment_value || 0;
      } else if (status === 'overdue') {
        overdueCount++;
      }
    }
    
    return { paidCount, overdueCount, paidAmount };
  };

  // Format date without timezone issues
  const formatDateBR = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Download document
  const handleDocumentDownload = async (doc) => {
    try {
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}${doc.file_url}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Erro ao baixar documento');
    }
  };

  // Download boleto
  const handleBoletoDownload = async (contractId, installmentIndex, boletoName) => {
    setDownloadingBoleto(`${contractId}-${installmentIndex}`);
    try {
      // Use current origin to ensure correct URL in both production and staging
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}/api/contracts/${contractId}/installments/${installmentIndex}/boleto`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', boletoName || `boleto_parcela_${installmentIndex + 1}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading boleto:', error);
      alert('Erro ao baixar boleto');
    } finally {
      setDownloadingBoleto(null);
    }
  };

  // Download contract file
  const handleContractDownload = async (contract) => {
    setDownloadingContract(contract.id);
    try {
      // Use current origin to ensure correct URL in both production and staging
      const baseUrl = window.location.origin;
      const response = await axios.get(
        `${baseUrl}${contract.contract_file}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract.contract_filename || 'contrato.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
      alert('Erro ao baixar contrato');
    } finally {
      setDownloadingContract(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Meus Contratos</h1>
        <p className="text-gray-600">Visualize seus contratos e acompanhe o status das parcelas</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue mx-auto"></div>
          <p className="text-gray-600 mt-4">Carregando contratos...</p>
        </div>
      ) : contracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FileSignature size={64} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Nenhum contrato encontrado</h2>
          <p className="text-gray-600">Você ainda não possui contratos cadastrados.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {contracts.map((contract) => {
            const statusConfig = getStatusConfig(contract.status);
            const stats = calculateContractStats(contract);
            const docs = contractDocuments[contract.id] || [];
            const isExpanded = expandedContract === contract.id;
            
            return (
              <div key={contract.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Contract Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-arcano-blue p-3 rounded-lg">
                        <FileSignature size={24} className="text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">
                          Contrato - R$ {contract.total_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </h2>
                        <p className="text-gray-600">{contract.installments}x de R$ {contract.installment_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusConfig.color}`}>
                        {statusConfig.label}
                      </span>
                      {contract.contract_file && (
                        <button
                          onClick={() => handleContractDownload(contract)}
                          disabled={downloadingContract === contract.id}
                          className="flex items-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors disabled:opacity-50"
                        >
                          {downloadingContract === contract.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <Download size={18} />
                              Baixar Contrato
                            </>
                          )}
                        </button>
                      )}
                      {contract.contract_url && !contract.contract_file && (
                        <a
                          href={contract.contract_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-arcano-blue hover:underline"
                        >
                          <ExternalLink size={18} />
                          Ver Contrato
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{stats.paidCount}</p>
                      <p className="text-sm text-green-600">Parcelas Pagas</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-700">{stats.overdueCount}</p>
                      <p className="text-sm text-red-600">Em Atraso</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">
                        R$ {stats.paidAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-blue-600">Total Pago</p>
                    </div>
                  </div>
                </div>

                {/* Installments */}
                <div className="p-6 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Parcelas</h3>
                  <div className="space-y-3">
                    {contract.due_dates?.map((date, index) => {
                      const paymentStatus = getPaymentStatus(contract, index);
                      const paymentConfig = getPaymentStatusConfig(paymentStatus);
                      const PaymentIcon = paymentConfig.icon;
                      
                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-4 rounded-lg ${paymentConfig.bg}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              paymentStatus === 'paid' ? 'bg-green-500' :
                              paymentStatus === 'overdue' ? 'bg-red-500' :
                              'bg-yellow-500'
                            }`}>
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Parcela {index + 1} de {contract.installments}</p>
                              <p className="text-sm text-gray-600">
                                R$ {contract.installment_value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">
                                <Calendar size={14} className="inline mr-1" />
                                {formatDateBR(date)}
                              </p>
                              <div className={`flex items-center gap-1 text-sm ${paymentConfig.color}`}>
                                <PaymentIcon size={14} />
                                <span>{paymentConfig.label}</span>
                              </div>
                            </div>
                            
                            {/* Boleto Download Button */}
                            {contract.installment_boletos?.[index] && (
                              <button
                                onClick={() => handleBoletoDownload(
                                  contract.id, 
                                  index, 
                                  contract.installment_boletos[index].original_name
                                )}
                                disabled={downloadingBoleto === `${contract.id}-${index}`}
                                className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 text-sm"
                                title="Baixar Boleto"
                              >
                                {downloadingBoleto === `${contract.id}-${index}` ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                  <>
                                    <Receipt size={16} />
                                    Boleto
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Documents Section */}
                <div className="p-6">
                  <button
                    onClick={() => toggleContractDocuments(contract.id)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FileText size={20} className="text-arcano-blue" />
                      Documentos do Contrato
                      {docs.length > 0 && (
                        <span className="px-2 py-0.5 bg-arcano-blue text-white text-xs rounded-full">
                          {docs.length}
                        </span>
                      )}
                    </h3>
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-4">
                      {docs.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">Nenhum documento anexado a este contrato</p>
                      ) : (
                        <div className="space-y-2">
                          {docs.map((doc) => (
                            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                              <div className="flex items-center gap-3">
                                <FileText size={20} className="text-gray-500" />
                                <div>
                                  <p className="font-medium">{doc.filename}</p>
                                  <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <span>{new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}</span>
                                    {doc.file_size && <span>• {formatFileSize(doc.file_size)}</span>}
                                    {doc.description && <span>• {doc.description}</span>}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDocumentDownload(doc)}
                                className="flex items-center gap-2 px-4 py-2 bg-arcano-blue text-white rounded-md hover:bg-arcano-blue-dark transition-colors"
                              >
                                <Download size={18} />
                                Baixar
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Contracts;
